import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, Button, Typography } from '@mui/material';

import PageContent from 'layouts/PageContent';
import RolePermissionForm from 'components/role-and-permissions/StepperForm/RolePermissionForm';
import AuditLogDialog from 'pages/role-and-permissions/AuditLogDialog';
import {
  transformMenuDetailToTabsConfig,
  filterMenuDetailBySelected,
} from 'components/role-and-permissions/utils/transformMenuDetail';
import {
  getMenuDetails,
  getAuditLogs,
} from 'app/features/roles-and-permissions/rolesAndPermissionsSaga';


function ViewRole() {
  const { roleId } = useParams();
  const dispatch = useDispatch();
  const { menuDetails, auditLogs } = useSelector((state) => state.rolesAndPermissions);

  const [tabIndex, setTabIndex] = useState(0);
  const [showAuditLogs, setShowAuditLogs] = useState(false);

  useEffect(() => {
    if (roleId) {
      dispatch(getMenuDetails(roleId));
    }
  }, [dispatch, roleId]);

  const tabsConfig = useMemo(
    () => transformMenuDetailToTabsConfig(filterMenuDetailBySelected(menuDetails?.data || [])),
    [menuDetails],
  );

  const defaultValues = useMemo(() => {
    return Object.fromEntries(
      tabsConfig.flatMap(
        (tab) =>
          tab.subMenus?.flatMap((sub) => {
            const enableAll = [
              [
                `menu_${tab.id}_${sub.id}_enable_all`,
                sub.permissions?.every((p) => p.isSelected) ?? false,
              ],
            ];

            const permissions =
              sub.permissions?.map((perm) => [
                `menu_${tab.id}_${sub.id}_${perm.id}`,
                perm.isSelected ?? false,
              ]) ?? [];

            return [...enableAll, ...permissions];
          }) ?? [],
      ),
    );
  }, [tabsConfig]);

  const methods = useForm({
    defaultValues,
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const fetchPage = (pageNumber) => {
    dispatch(getAuditLogs({ key: roleId, pageNumber, pageSize: 10 }));
  };

  const handleViewAuditLogs = () => {
    setShowAuditLogs(true);
    dispatch(getAuditLogs({ key: roleId, pageNumber: 1, pageSize: 10 }));
  };

  return (
    <PageContent title="View Role Permissions">
      <FormProvider {...methods}>
        <Box mt={2}>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button variant="contained" onClick={handleViewAuditLogs}>
              View Audit Log
            </Button>
          </Box>

          {tabsConfig.length === 0 ? (
            <Box bgcolor="#f5f5f5" p={3} borderRadius={2}>
              <Typography color="textSecondary" variant="body1">
                This role currently has no permissions assigned.
              </Typography>
            </Box>
          ) : (
            <RolePermissionForm
              tabsConfig={tabsConfig}
              tabsLoading={menuDetails?.isLoading}
              activeTab={tabIndex}
              onTabChange={setTabIndex}
              readOnly
            />
          )}

          <AuditLogDialog
            open={showAuditLogs}
            onClose={() => setShowAuditLogs(false)}
            isLoading={auditLogs?.isLoading}
            logs={auditLogs?.data || []}
            pagination={{
              currentPage: auditLogs?.pagination?.currentPage,
              totalPage: auditLogs?.pagination?.totalPage,
            }}
            onPageChange={fetchPage}
          />
        </Box>
      </FormProvider>
    </PageContent>
  );
}

export default ViewRole;
